import { CoreModule } from '@abp/ng.core';
import { AbpOAuthModule } from '@abp/ng.oauth';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import localeEn from '@angular/common/locales/en';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgIdleModule } from '@ng-idle/core';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { AccountAdminConfigModule } from '@volo/abp.ng.account/admin/config';
import { AccountPublicConfigModule } from '@volo/abp.ng.account/public/config';
import { SaasConfigModule } from '@volo/abp.ng.saas/config';
import { TourNgxBootstrapModule } from 'ngx-ui-tour-ngx-bootstrap';
import { environment } from '../environments/environment';
import { AppInterceptorInterceptor } from './app-interceptor.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LogUpdateService } from './core/services/logUpdate.service';
import { LayoutsModule } from './layouts/layouts.module';
import { RootComponent } from './root/root.component';
import { rootReducer } from './store';

export function createTranslateLoader(http: HttpClient): any {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

registerLocaleData(localeEn);

export function registerLocaleFn(locale: string): Promise<{ default: any } | undefined> {
  const localeKey = (locale || 'en').toLowerCase();

  if (localeKey === 'ch' || localeKey === 'zh' || localeKey.startsWith('zh-')) {
    return import('@angular/common/locales/zh-Hans').then(m => ({ default: m.default ?? m }));
  }

  if (localeKey === 'ar' || localeKey.startsWith('ar-')) {
    return import('@angular/common/locales/ar').then(m => ({ default: m.default ?? m }));
  }

  if (localeKey === 'de' || localeKey.startsWith('de-')) {
    return import('@angular/common/locales/de').then(m => ({ default: m.default ?? m }));
  }

  if (localeKey === 'es' || localeKey.startsWith('es-')) {
    return import('@angular/common/locales/es').then(m => ({ default: m.default ?? m }));
  }

  if (localeKey === 'fr' || localeKey.startsWith('fr-')) {
    return import('@angular/common/locales/fr').then(m => ({ default: m.default ?? m }));
  }

  if (localeKey === 'it' || localeKey.startsWith('it-')) {
    return import('@angular/common/locales/it').then(m => ({ default: m.default ?? m }));
  }

  if (localeKey === 'ru' || localeKey.startsWith('ru-')) {
    return import('@angular/common/locales/ru').then(m => ({ default: m.default ?? m }));
  }

  return Promise.resolve({ default: localeEn });
}

@NgModule({
  declarations: [
    AppComponent,
    RootComponent,
  ],
  imports: [
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    CoreModule.forRoot({
      environment,
      registerLocaleFn,
    }),
    AbpOAuthModule.forRoot(),
    StoreModule.forRoot(rootReducer),
    BrowserAnimationsModule,
    HttpClientModule,
    BrowserModule,
    AppRoutingModule,
    DialogModule,
    AccountAdminConfigModule.forRoot(),
    AccountPublicConfigModule.forRoot(),
    SaasConfigModule.forRoot(),
    LayoutsModule,
    NotificationModule,
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerImmediately'
    }),
     NgIdleModule.forRoot(),
     NgIdleKeepaliveModule.forRoot(),
    IndicatorsModule
  ],
  providers: [
    TourNgxBootstrapModule,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppInterceptorInterceptor,
      multi: true
    },
    LogUpdateService
  ],
  bootstrap: [RootComponent],
})
export class AppModule { }
